import React, { useState, useEffect } from 'react';
import { Users, Plus, Trash2, X } from 'lucide-react';
import { usersAPI } from '../../services/api';
import './AdminSections.css';

const ROLE_LABEL = { admin: 'Admin', engineer: 'Μηχανικός', customer: 'Πελάτης' };

const emptyForm = {
  username: '', email: '', first_name: '', last_name: '',
  phone: '', afm: '', password: '', role: 'engineer'
};

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [filterRole, setFilterRole] = useState('all');

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await usersAPI.getAll();
      setUsers(response.data);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        full_name: `${formData.first_name} ${formData.last_name}`.trim() || formData.username,
      };
      await usersAPI.create(payload);
      setShowModal(false);
      setFormData(emptyForm);
      loadUsers();
    } catch (error) {
      alert('Σφάλμα: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Είστε σίγουροι ότι θέλετε να διαγράψετε αυτόν τον χρήστη;')) return;

    try {
      await usersAPI.delete(userId);
      loadUsers();
    } catch (error) {
      alert('Σφάλμα: ' + (error.response?.data?.detail || 'Δεν μπορείτε να διαγράψετε τον εαυτό σας'));
    }
  };

  const filteredUsers = filterRole === 'all' ? users : users.filter(u => u.role === filterRole);

  return (
    <div className="admin-section" data-testid="user-management">
      <div className="section-header">
        <h2>
          <Users size={24} />
          Διαχείριση Χρηστών
        </h2>
        <button className="btn-primary" onClick={() => setShowModal(true)} data-testid="create-user-btn">
          <Plus size={18} />
          Δημιουργία Χρήστη
        </button>
      </div>

      <div style={{ marginBottom: '1.5rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        {['all', 'admin', 'engineer', 'customer'].map(r => (
          <button key={r} onClick={() => setFilterRole(r)}
            style={{
              padding: '0.5rem 1rem',
              border: filterRole === r ? '1px solid #667eea' : '1px solid #d0d0d0',
              background: filterRole === r ? '#667eea' : '#fff',
              color: filterRole === r ? '#fff' : '#666',
              cursor: 'pointer', fontSize: '0.85rem'
            }}
            data-testid={`filter-${r}`}
          >
            {r === 'all' ? 'Όλοι' : ROLE_LABEL[r]}
          </button>
        ))}
      </div>

      <div className="users-table">
        <table>
          <thead>
            <tr>
              <th>Username</th>
              <th>Password</th>
              <th>Ονοματεπώνυμο</th>
              <th>Email</th>
              <th>Τηλέφωνο</th>
              <th>ΑΦΜ</th>
              <th>Κωδ. Πελάτη</th>
              <th>Ρόλος</th>
              <th>Ενέργειες</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((user) => (
              <tr key={user.id}>
                <td><strong>{user.username}</strong></td>
                <td><code style={{ background: '#f5f5f5', padding: '0.2rem 0.5rem', fontSize: '0.85rem' }}>{user.plain_password || '***'}</code></td>
                <td>{user.first_name || user.last_name ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : (user.full_name || '-')}</td>
                <td>{user.email || '-'}</td>
                <td>{user.phone || '-'}</td>
                <td>{user.afm || '-'}</td>
                <td>{user.customer_number ? <span style={{ background: '#e3f2fd', color: '#1976d2', padding: '0.2rem 0.5rem', fontSize: '0.8rem', borderRadius: 3 }}>{user.customer_number}</span> : '-'}</td>
                <td>
                  <span className={`role-badge ${user.role}`}>
                    {ROLE_LABEL[user.role] || user.role}
                  </span>
                </td>
                <td>
                  <button
                    className="btn-icon danger"
                    onClick={() => handleDelete(user.id)}
                    disabled={user.role === 'admin'}
                    data-testid={`delete-user-${user.username}`}
                  >
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay-admin" onClick={() => setShowModal(false)}>
          <div className="modal-admin" style={{ maxWidth: 640 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-admin">
              <h3>Δημιουργία Νέου Χρήστη</h3>
              <button onClick={() => setShowModal(false)}><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit} className="modal-form" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div className="form-group-admin">
                  <label>Όνομα *</label>
                  <input type="text" value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    required data-testid="user-first-name" />
                </div>
                <div className="form-group-admin">
                  <label>Επώνυμο *</label>
                  <input type="text" value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    required data-testid="user-last-name" />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div className="form-group-admin">
                  <label>Τηλέφωνο</label>
                  <input type="tel" value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="π.χ. 6900000000" data-testid="user-phone" />
                </div>
                <div className="form-group-admin">
                  <label>Email</label>
                  <input type="email" value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    data-testid="user-email" />
                </div>
              </div>

              <div className="form-group-admin">
                <label>ΑΦΜ</label>
                <input type="text" value={formData.afm}
                  onChange={(e) => setFormData({ ...formData, afm: e.target.value })}
                  placeholder="123456789" data-testid="user-afm" />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div className="form-group-admin">
                  <label>Username *</label>
                  <input type="text" value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    required data-testid="user-username" />
                </div>
                <div className="form-group-admin">
                  <label>Κωδικός *</label>
                  <input type="text" value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required minLength="6" data-testid="user-password" />
                </div>
              </div>

              <div className="form-group-admin">
                <label>Ρόλος</label>
                <select value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  data-testid="user-role">
                  <option value="engineer">Μηχανικός</option>
                  <option value="admin">Admin</option>
                  <option value="customer">Πελάτης (αυτόματος κωδ.)</option>
                </select>
                {formData.role === 'customer' && (
                  <p style={{ fontSize: '0.75rem', color: '#999', marginTop: 4 }}>
                    Θα δημιουργηθεί αυτόματα κωδικός πελάτη CUST-XXXX
                  </p>
                )}
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>
                  Ακύρωση
                </button>
                <button type="submit" className="btn-primary" disabled={loading} data-testid="user-submit-btn">
                  {loading ? 'Δημιουργία...' : 'Δημιουργία'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
